import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import '../index.css';

function Documents() {
  const { hasPermission } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [statusSearchText, setStatusSearchText] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [showUploadEmployeeDropdown, setShowUploadEmployeeDropdown] = useState(false);
  const [uploadEmployeeSearchText, setUploadEmployeeSearchText] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categorySearchText, setCategorySearchText] = useState('');
  const [showSubCategoryDropdown, setShowSubCategoryDropdown] = useState(false);
  const [subCategorySearchText, setSubCategorySearchText] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployeeDocs, setSelectedEmployeeDocs] = useState([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [viewDocument, setViewDocument] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewDocumentUrl, setViewDocumentUrl] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    employeeId: '',
    title: '',
    description: '',
    category: 'Other',
    subCategory: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    category: 'Other'
  });
  const canUploadForEmployee = hasPermission('documents', 'create') || hasPermission('my-documents', 'create');
  const canCreate = hasPermission('documents', 'create');
  const canDelete = hasPermission('documents', 'delete');
  const canApprove = hasPermission('documents', 'update');

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/documents');
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchEmployees();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.custom-status-dropdown')) setShowStatusDropdown(false);
      if (!e.target.closest('.custom-employee-dropdown')) setShowEmployeeDropdown(false);
      if (!e.target.closest('.custom-upload-employee-dropdown')) setShowUploadEmployeeDropdown(false);
      if (!e.target.closest('.custom-category-dropdown')) setShowCategoryDropdown(false);
      if (!e.target.closest('.custom-subcategory-dropdown')) setShowSubCategoryDropdown(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const filtered = documents.filter((doc) => {
      const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
      const matchesEmployee = employeeFilter === 'all' || doc.uploadedBy?._id === employeeFilter;
      const text = `${doc.title || ''} ${doc.description || ''} ${doc.category || ''}`.toLowerCase();
      const matchesSearch = !searchTerm || text.includes(searchTerm.toLowerCase());
      return matchesStatus && matchesEmployee && matchesSearch;
    });

    setFilteredDocuments(filtered);
  }, [documents, statusFilter, searchTerm, employeeFilter]);

  const handleDownload = async (id, filename) => {
    try {
      const response = await api.get(`/documents/${id}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error downloading document');
      console.error('Download error:', error);
    }
  };

  const formatDocumentDate = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusMeta = (status) => {
    const map = {
      pending: { bg: 'rgba(245,158,11,0.15)', color: '#92400e' },
      approved: { bg: 'rgba(16,185,129,0.15)', color: '#065f46' },
      rejected: { bg: 'rgba(239,68,68,0.15)', color: '#991b1b' }
    };
    return map[status] || { bg: '#f8fafc', color: '#475569' };
  };

  const handleView = async (doc) => {
    setViewDocument(doc);
    setViewLoading(true);

    // Revoke previous blob URL to free memory
    if (viewDocumentUrl) {
      URL.revokeObjectURL(viewDocumentUrl);
      setViewDocumentUrl(null);
    }

    try {
      // Try the dedicated /preview endpoint first (streams with Content-Disposition: inline)
      // Fall back to /download if preview endpoint not available (old server)
      let response;
      try {
        response = await api.get(`/documents/${doc._id}/preview`, { responseType: 'blob' });
      } catch (previewErr) {
        // Fallback to download endpoint if preview route not yet deployed
        response = await api.get(`/documents/${doc._id}/download`, { responseType: 'blob' });
      }

      const mimeType = response.headers['content-type'] || doc.mimetype || 'application/octet-stream';
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      setViewDocumentUrl(blobUrl);
    } catch (error) {
      console.error('View document error:', error);
      setViewDocumentUrl(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleViewEmployee = (employee, docs) => {
    setSelectedEmployee(employee);
    setSelectedEmployeeDocs(docs || []);
    setShowEmployeeModal(true);
  };

  const handleEdit = (doc) => {
    setEditingDocument(doc);
    setEditFormData({
      title: doc.title || '',
      description: doc.description || '',
      category: doc.category || 'Other'
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingDocument) return;

    try {
      await api.put(`/documents/my-documents/${editingDocument._id}`, editFormData);
      setShowEditModal(false);
      setEditingDocument(null);
      fetchDocuments();
      alert('Document updated successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Error updating document');
      console.error('Update error:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.delete(`/documents/${id}`);
      alert('Document deleted successfully.');
      fetchDocuments();
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting document');
      console.error('Delete error:', error);
    }
  };

  const handleUpdateStatus = async (documentId, status) => {
    if (!canApprove) {
      alert('You do not have permission to approve or reject documents.');
      return;
    }

    let rejectionReason;
    if (status === 'rejected') {
      rejectionReason = window.prompt('Please provide a reason for rejection:');
      if (rejectionReason === null) {
        return; // User cancelled the prompt
      }
      if (!rejectionReason.trim()) {
        alert('Rejection reason is required.');
        return;
      }
    }

    try {
      const response = await api.put(`/documents/${documentId}/status`, {
        status,
        rejectionReason
      });
      const updatedDocument = response.data;
      fetchDocuments();
      setSelectedEmployeeDocs((prevDocs) => prevDocs.map((doc) =>
        doc._id === documentId ? { ...doc, status: updatedDocument.status, rejectionReason: updatedDocument.rejectionReason } : doc
      ));
      if (viewDocument && viewDocument._id === documentId) {
        setViewDocument((prev) => prev ? { ...prev, status: updatedDocument.status, rejectionReason: updatedDocument.rejectionReason } : prev);
      }
      alert(`Document ${status === 'approved' ? 'approved' : 'rejected'} successfully.`);
    } catch (error) {
      alert(error.response?.data?.message || 'Error updating document status');
      console.error('Status update error:', error);
    }
  };

  useEffect(() => {
    return () => {
      if (viewDocumentUrl) {
        URL.revokeObjectURL(viewDocumentUrl);
      }
    };
  }, [viewDocumentUrl]);

  const closeViewDocument = () => {
    if (viewDocumentUrl) {
      URL.revokeObjectURL(viewDocumentUrl);
      setViewDocumentUrl(null);
    }
    setViewDocument(null);
  };

  const handleUploadForEmployee = async (e) => {
    e.preventDefault();

    if (!uploadFormData.employeeId || !uploadFormData.file) {
      alert('Please select an employee and choose a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', uploadFormData.file);
      formData.append('employeeId', uploadFormData.employeeId);
      formData.append('title', uploadFormData.title || uploadFormData.file.name);
      formData.append('description', uploadFormData.description);
      formData.append('category', uploadFormData.category);
      if (uploadFormData.subCategory) formData.append('subCategory', uploadFormData.subCategory);

      await api.post('/documents/upload-for-employee', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Document uploaded successfully!');
      setShowUploadModal(false);
      setUploadFormData({
        employeeId: '',
        title: '',
        description: '',
        category: 'Other',
        subCategory: '',
        file: null
      });
      fetchDocuments();
    } catch (error) {
      alert(error.response?.data?.message || 'Error uploading document');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      approved: 'badge-success',
      rejected: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Aadhar Card': '🆔',
      'PAN Card': '💳',
      'Bank Account': '🏦',
      'Passport': '🛂',
      'Driving License': '🚗',
      'Marksheet': '📜',
      'Degree Certificate': '🎓',
      'Experience Letter': '📋',
      'Offer Letter': '💼',
      'Appointment Letter': '📑',
      'Relieving Letter': '📄',
      'Policy': '📋',
      'Contract': '📝',
      'Report': '📊',
      'Other': '📄'
    };
    return icons[category] || '📄';
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  // Calculate statistics
  const totalDocuments = documents.length;
  const pendingApproval = documents.filter(doc => doc.status === 'pending').length;
  const approvedDocuments = documents.filter(doc => doc.status === 'approved').length;
  const rejectedDocuments = documents.filter(doc => doc.status === 'rejected').length;

  return (
    <div className="page-container" style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: '#f8fafc',
      padding: '28px 28px 40px',
      fontFamily: "'Inter','Poppins','Segoe UI',sans-serif"
    }}>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }
        @keyframes doc-bubble-float-large {
          0% { transform: translate(0px, 0px) scale(1); }
          30% { transform: translate(-5px, -7px) scale(1.04); }
          60% { transform: translate(4px, -4px) scale(0.97); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        @keyframes doc-bubble-float-small {
          0% { transform: translate(0px, 0px) scale(1); }
          35% { transform: translate(6px, -9px) scale(1.06); }
          65% { transform: translate(-4px, -3px) scale(0.95); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .doc-metrics-grid > div { position: relative; overflow: hidden; isolation: isolate; cursor: pointer; transition: all 0.2s ease; }
        .doc-metrics-grid > div:hover { transform: translateY(-2px); box-shadow: 0 8px 16px rgba(0,0,0,0.08); }
        .doc-metrics-grid > div::before {
          content: ""; position: absolute; z-index: 0; width: 90px; height: 90px;
          border-radius: 50%; opacity: 0.06; right: -26px; bottom: -26px;
          pointer-events: none; animation: doc-bubble-float-large 5.5s ease-in-out infinite;
        }
        .doc-metrics-grid > div::after {
          content: ""; position: absolute; z-index: 0; width: 54px; height: 54px;
          border-radius: 50%; opacity: 0.04; right: 36px; bottom: -20px;
          pointer-events: none; animation: doc-bubble-float-small 4.2s ease-in-out infinite;
        }
        .doc-metrics-grid > div:nth-child(1)::before, .doc-metrics-grid > div:nth-child(1)::after { background: #667eea; }
        .doc-metrics-grid > div:nth-child(2)::before, .doc-metrics-grid > div:nth-child(2)::after { background: #f59e0b; }
        .doc-metrics-grid > div:nth-child(3)::before, .doc-metrics-grid > div:nth-child(3)::after { background: #10b981; }
        .doc-metrics-grid > div:nth-child(4)::before, .doc-metrics-grid > div:nth-child(4)::after { background: #ef4444; }
        .doc-metrics-grid > div:nth-child(2)::before { animation-delay: 0.8s; }
        .doc-metrics-grid > div:nth-child(3)::before { animation-delay: 1.5s; }
        .doc-metrics-grid > div:nth-child(4)::before { animation-delay: 2.2s; }
        .doc-metrics-grid > div:nth-child(2)::after { animation-delay: 1.2s; }
        .doc-metrics-grid > div:nth-child(3)::after { animation-delay: 0.5s; }
        .doc-metrics-grid > div:nth-child(4)::after { animation-delay: 1.8s; }
      `}</style>

      {/* ── Simplified Page Header ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '22px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: 600,
            color: '#0f172a',
            letterSpacing: '-0.025em'
          }}>
            Document Management
          </h1>
          <p style={{
            margin: '4px 0 0',
            fontSize: '13px',
            color: '#94a3b8',
            fontWeight: 500
          }}>
            View, approve and manage all employee documents
          </p>
        </div>
      </div>

      {/* Professional Statistics Cards */}
      <div className="doc-metrics-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '14px',
        marginBottom: '22px'
      }}>
        <div
          onClick={() => setStatusFilter('all')}
          style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px 18px'
          }}
        >
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: '12px',
              color: '#64748b',
              fontWeight: 600,
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Total Documents
            </div>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: '#667eea',
              lineHeight: 1,
              marginBottom: '8px'
            }}>
              {totalDocuments}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>
              All documents in system
            </div>
          </div>
        </div>

        <div
          onClick={() => setStatusFilter('pending')}
          style={{
            background: '#fff',
            border: pendingApproval > 0 ? '2px solid #f59e0b' : '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px 18px',
            position: 'relative'
          }}
        >
          {pendingApproval > 0 && (
            <div style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              background: '#f59e0b',
              color: '#fff',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
              animation: 'pulse 2s infinite'
            }}>
              !
            </div>
          )}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: '12px',
              color: '#64748b',
              fontWeight: 600,
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Pending Approval
            </div>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: '#f59e0b',
              lineHeight: 1,
              marginBottom: '8px'
            }}>
              {pendingApproval}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>
              {pendingApproval > 0 ? 'Requires your action' : 'No pending documents'}
            </div>
          </div>
        </div>

        <div
          onClick={() => setStatusFilter('approved')}
          style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px 18px'
          }}
        >
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: '12px',
              color: '#64748b',
              fontWeight: 600,
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              Approved
            </div>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: '#10b981',
              lineHeight: 1,
              marginBottom: '8px'
            }}>
              {approvedDocuments}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>
              Successfully approved
            </div>
          </div>
        </div>

        <div
          onClick={() => setStatusFilter('rejected')}
          style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px 18px'
          }}
        >
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: '12px',
              color: '#64748b',
              fontWeight: 600,
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              Rejected
            </div>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: '#ef4444',
              lineHeight: 1,
              marginBottom: '8px'
            }}>
              {rejectedDocuments}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>
              {rejectedDocuments > 0 ? 'Needs revision' : 'No rejections'}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Button and Quick Filters for HR/Manager */}
      {canUploadForEmployee && (
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setStatusFilter('all')}
              style={{
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: 600,
                border: statusFilter === 'all' ? 'none' : '1px solid #e2e8f0',
                borderRadius: '8px',
                background: statusFilter === 'all' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
                color: statusFilter === 'all' ? '#fff' : '#64748b',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              All ({totalDocuments})
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              style={{
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: 600,
                border: statusFilter === 'pending' ? 'none' : '1px solid #e2e8f0',
                borderRadius: '8px',
                background: statusFilter === 'pending' ? '#f59e0b' : '#fff',
                color: statusFilter === 'pending' ? '#000' : '#64748b',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: statusFilter === 'pending' && pendingApproval > 0 ? '0 4px 12px rgba(245, 158, 11, 0.3)' : 'none'
              }}
            >
              Pending ({pendingApproval})
            </button>
            <button
              onClick={() => setStatusFilter('approved')}
              style={{
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: 600,
                border: statusFilter === 'approved' ? 'none' : '1px solid #e2e8f0',
                borderRadius: '8px',
                background: statusFilter === 'approved' ? '#10b981' : '#fff',
                color: statusFilter === 'approved' ? '#fff' : '#64748b',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              Approved ({approvedDocuments})
            </button>
            <button
              onClick={() => setStatusFilter('rejected')}
              style={{
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: 600,
                border: statusFilter === 'rejected' ? 'none' : '1px solid #e2e8f0',
                borderRadius: '8px',
                background: statusFilter === 'rejected' ? '#ef4444' : '#fff',
                color: statusFilter === 'rejected' ? '#fff' : '#64748b',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              Rejected ({rejectedDocuments})
            </button>
          </div>
          {canCreate && <button
            onClick={() => setShowUploadModal(true)}
            style={{
              padding: '10px 18px',
              fontSize: '13px',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              border: 'none',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b775e 0%, #56a07a 100%)',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: "'Inter','Poppins','Segoe UI',sans-serif"
            }}
          >
            Upload Document for Employee
          </button>}
        </div>
      )}

      {/* Search and Filters */}
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '18px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Search Documents
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by title, description, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="custom-status-dropdown" style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Filter by Status
            </label>
            <button
              type="button"
              onClick={() => { setShowStatusDropdown(!showStatusDropdown); setShowEmployeeDropdown(false); }}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: showStatusDropdown ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                borderRadius: '8px',
                background: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: showStatusDropdown ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                transition: 'all 0.2s',
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                color: '#0f172a'
              }}
            >
              <span>
                {statusFilter === 'all' ? 'All Documents' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: showStatusDropdown ? 'rotate(180deg)' : 'none' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showStatusDropdown && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                width: '100%',
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                zIndex: 300,
                padding: '8px',
                border: '1px solid #f1f5f9',
                animation: 'pulse 0.2s ease-out'
              }}>
                <input
                  type="text"
                  placeholder="Search status..."
                  value={statusSearchText}
                  onChange={(e) => setStatusSearchText(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '13px',
                    background: '#f8fafc',
                    outline: 'none',
                    marginBottom: '8px',
                    fontFamily: "'Inter', sans-serif"
                  }}
                />
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {[{ value: 'all', label: 'All Documents' }, { value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }]
                    .filter(s => s.label.toLowerCase().includes(statusSearchText.toLowerCase()))
                    .map(status => (
                      <div
                        key={status.value}
                        onClick={() => { setStatusFilter(status.value); setShowStatusDropdown(false); setStatusSearchText(''); }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: statusFilter === status.value ? '600' : '400',
                          cursor: 'pointer',
                          color: statusFilter === status.value ? '#166534' : '#334155',
                          background: statusFilter === status.value ? '#dcfce7' : 'transparent',
                          marginBottom: '2px',
                          transition: 'background 0.1s'
                        }}
                        onMouseEnter={(e) => { if (statusFilter !== status.value) e.target.style.background = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (statusFilter !== status.value) e.target.style.background = 'transparent'; }}
                      >
                        {status.label}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
          <div className="custom-employee-dropdown" style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Filter by Employee
            </label>
            <button
              type="button"
              onClick={() => { setShowEmployeeDropdown(!showEmployeeDropdown); setShowStatusDropdown(false); }}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: showEmployeeDropdown ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                borderRadius: '8px',
                background: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: showEmployeeDropdown ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                transition: 'all 0.2s',
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                color: '#0f172a'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }}>
                {employeeFilter === 'all' ? 'All Employees' : (() => {
                  const emp = employees.find(e => e._id === employeeFilter);
                  return emp ? `${emp.firstName} ${emp.lastName}` : 'All Employees';
                })()}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: showEmployeeDropdown ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showEmployeeDropdown && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                width: '100%',
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                zIndex: 300,
                padding: '8px',
                border: '1px solid #f1f5f9',
                animation: 'pulse 0.2s ease-out'
              }}>
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={employeeSearchText}
                  onChange={(e) => setEmployeeSearchText(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '13px',
                    background: '#f8fafc',
                    outline: 'none',
                    marginBottom: '8px',
                    fontFamily: "'Inter', sans-serif"
                  }}
                />
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <div
                    onClick={() => { setEmployeeFilter('all'); setShowEmployeeDropdown(false); setEmployeeSearchText(''); }}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: employeeFilter === 'all' ? '600' : '400',
                      cursor: 'pointer',
                      color: employeeFilter === 'all' ? '#166534' : '#334155',
                      background: employeeFilter === 'all' ? '#dcfce7' : 'transparent',
                      marginBottom: '2px',
                      transition: 'background 0.1s'
                    }}
                    onMouseEnter={(e) => { if (employeeFilter !== 'all') e.target.style.background = '#f1f5f9'; }}
                    onMouseLeave={(e) => { if (employeeFilter !== 'all') e.target.style.background = 'transparent'; }}
                  >
                    All Employees
                  </div>
                  {employees
                    .filter(emp => `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(employeeSearchText.toLowerCase()))
                    .map(emp => (
                      <div
                        key={emp._id}
                        onClick={() => { setEmployeeFilter(emp._id); setShowEmployeeDropdown(false); setEmployeeSearchText(''); }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: employeeFilter === emp._id ? '600' : '400',
                          cursor: 'pointer',
                          color: employeeFilter === emp._id ? '#166534' : '#334155',
                          background: employeeFilter === emp._id ? '#dcfce7' : 'transparent',
                          marginBottom: '2px',
                          transition: 'background 0.1s'
                        }}
                        onMouseEnter={(e) => { if (employeeFilter !== emp._id) e.target.style.background = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (employeeFilter !== emp._id) e.target.style.background = 'transparent'; }}
                      >
                        {emp.firstName} {emp.lastName} <span style={{ fontSize: '12px', color: '#64748b' }}>({emp.employeeId})</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Documents Table */}
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(15,23,42,0.08)',
        marginBottom: '20px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}>
        {filteredDocuments.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', overflowY: 'auto', flex: 1 }}>
            <p style={{ color: '#64748b', fontSize: '16px' }}>No documents found</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px', fontFamily: "'Inter','Poppins','Segoe UI',sans-serif" }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff' }}>
                <tr>
                  {['#', 'Document', 'Category', 'Employee', 'Status', 'Uploaded', 'Actions'].map((label) => (
                    <th
                      key={label}
                      style={{
                        textAlign: label === 'Actions' ? 'center' : 'left',
                        padding: '16px 18px',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        borderBottom: '1px solid #f1f5f9',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const grouped = filteredDocuments.reduce((acc, doc) => {
                    const id = doc.uploadedBy?._id || 'unknown';
                    if (!acc[id]) acc[id] = { employee: doc.uploadedBy, docs: [] };
                    acc[id].docs.push(doc);
                    return acc;
                  }, {});

                  const groups = Object.values(grouped);
                  if (groups.length === 0) {
                    return (
                      <tr>
                        <td colSpan={7} style={{ padding: '28px', textAlign: 'center', color: '#64748b' }}>No documents found</td>
                      </tr>
                    );
                  }

                  return groups.map((grp, gIdx) => {
                    const docs = grp.docs || [];
                    const latest = docs.reduce((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? a : b, docs[0]);
                    const pendingCount = docs.filter(d => d.status === 'pending').length;
                    const count = docs.length;
                    const first = docs[0] || {};

                    const categories = Array.from(new Set(docs.map(d => d.category || 'Other')));

                    return (
                      <tr key={grp.employee?._id || gIdx} style={{ background: gIdx % 2 === 0 ? '#fff' : '#fbfbfd' }}>
                        <td style={{ padding: '16px 18px', color: '#64748b', fontSize: '13px', width: '48px' }}>{gIdx + 1}</td>
                        <td style={{ padding: '16px 18px', maxWidth: '320px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{first.title || 'Untitled document'}{count > 1 && <span style={{ marginLeft: 8, fontSize: 12, color: '#64748b' }}>+{count - 1} more</span>}</span>
                            <span style={{ color: '#64748b', fontSize: '12px', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis' }}>{first.description || 'No description provided'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '16px 18px', color: '#334155', fontSize: '13px' }}>{categories.length === 1 ? categories[0] : 'Multiple'}</td>
                        <td style={{ padding: '16px 18px', color: '#334155', fontSize: '13px' }}>{grp.employee ? `${grp.employee.firstName} ${grp.employee.lastName}` : 'N/A'}</td>
                        <td style={{ padding: '16px 18px' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '7px 12px',
                            borderRadius: '999px',
                            background: pendingCount > 0 ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.08)',
                            color: pendingCount > 0 ? '#92400e' : '#065f46',
                            fontSize: '12px',
                            fontWeight: 700,
                            textTransform: 'capitalize'
                          }}>
                            {pendingCount > 0 ? `Pending (${pendingCount})` : `Approved (${count - pendingCount})`}
                          </span>
                        </td>
                        <td style={{ padding: '16px 18px', color: '#475569', fontSize: '13px' }}>{latest ? formatDocumentDate(latest.createdAt) : 'N/A'}</td>
                        <td style={{ padding: '16px 18px', textAlign: 'center', minWidth: '200px' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                            <button
                              className="view-btnn"
                              onClick={() => handleViewEmployee(grp.employee, docs)}
                              style={{
                                border: '1px solid #dbeafe',
                                background: '#eff6ff',
                                color: '#3730a3',
                                borderRadius: '10px',
                                padding: '8px 12px',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',


                              }}
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewDocument && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px',
            overflowY: 'auto'
          }}
          onClick={closeViewDocument}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '960px',
              borderRadius: '18px',
              background: '#fff',
              overflow: 'hidden',
              boxShadow: '0 24px 48px rgba(15,23,42,0.18)',
              maxHeight: 'calc(100vh - 40px)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >

            <div style={{ padding: '22px 24px', borderBottom: '1px solid #eef2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>View Document</h2>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#64748b' }}>Document details for the selected employee file.</p>
              </div>
              <button
                onClick={() => setViewDocument(null)}
                style={{ border: 'none', background: 'transparent', fontSize: '24px', color: '#64748b', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(360px, 1.2fr)', gap: '22px', overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {['Title', 'Category', 'Description', 'Employee', 'Status', 'Uploaded'].map((label) => {
                  const valueMap = {
                    Title: viewDocument.title || 'Untitled document',
                    Category: viewDocument.category || 'Other',
                    Description: viewDocument.description || 'No description provided',
                    Employee: viewDocument.uploadedBy ? `${viewDocument.uploadedBy.firstName} ${viewDocument.uploadedBy.lastName}` : 'N/A',
                    Status: viewDocument.status || 'Unknown',
                    Uploaded: formatDocumentDate(viewDocument.createdAt)
                  };
                  return (
                    <div key={label} style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '12px', alignItems: 'start' }}>
                      <strong style={{ color: '#64748b', fontSize: '13px' }}>{label}</strong>
                      <span style={{ color: '#0f172a', fontSize: '13px', lineHeight: 1.6 }}>{valueMap[label]}</span>
                    </div>
                  );
                })}

                {canApprove && viewDocument?.status === 'pending' && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => handleUpdateStatus(viewDocument._id, 'approved')}
                      style={{
                        border: '1px solid #a7f3d0',
                        background: '#d1fae5',
                        color: '#065f46',
                        borderRadius: '10px',
                        padding: '10px 14px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateStatus(viewDocument._id, 'rejected')}
                      style={{
                        border: '1px solid rgba(239,68,68,0.2)',
                        background: 'rgba(239,68,68,0.12)',
                        color: '#b91c1c',
                        borderRadius: '10px',
                        padding: '10px 14px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <strong style={{ fontSize: '13px', color: '#475569' }}>Preview</strong>
                <div style={{
                  minHeight: '320px',
                  borderRadius: '16px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px'
                }}>
                  {viewLoading ? (
                    <span style={{ color: '#64748b', fontSize: '13px' }}>Loading file preview...</span>
                  ) : viewDocumentUrl && (viewDocument.mimetype?.includes('pdf') || viewDocument.filename?.toLowerCase().endsWith('.pdf')) ? (
                    <iframe
                      title="Document Preview"
                      src={viewDocumentUrl}
                      style={{ width: '100%', height: '100%', border: 'none', minHeight: '320px' }}
                      allowFullScreen
                    />
                  ) : viewDocumentUrl && (viewDocument.mimetype?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(viewDocument.filename || '')) ? (
                    <img
                      src={viewDocumentUrl}
                      alt={viewDocument.filename}
                      style={{ width: '100%', height: 'auto', maxHeight: '520px', objectFit: 'contain', borderRadius: '6px' }}
                    />
                  ) : viewDocumentUrl ? (
                    <div style={{ color: '#475569', fontSize: '13px', textAlign: 'center' }}>
                      Preview unavailable for this file type. Use the Download button below.
                    </div>
                  ) : (
                    <div style={{ color: '#ef4444', fontSize: '13px', textAlign: 'center' }}>
                      Unable to load preview. Please download the file to view it.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ padding: '20px 24px', borderTop: '1px solid #eef2f7', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={closeViewDocument}
                style={{
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  color: '#475569',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleDownload(viewDocument._id, viewDocument.filename);
                  setViewDocument(null);
                }}
                style={{
                  border: 'none',
                  background: 'linear-gradient(135deg, #3b775e 0%, #56a07a 100%)',
                  color: '#fff',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Download
              </button>
            </div>
          </div>
        </div>
        , document.body)}

      {showEmployeeModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            overflowY: 'auto'
          }}
          onClick={() => setShowEmployeeModal(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '760px',
              borderRadius: '18px',
              background: '#fff',
              overflow: 'hidden',
              boxShadow: '0 24px 48px rgba(15,23,42,0.18)',
              maxHeight: 'calc(100vh - 40px)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #eef2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>{selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : 'Employee'} — Documents</h2>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#64748b' }}>All documents uploaded for this employee.</p>
              </div>
              <button
                onClick={() => setShowEmployeeModal(false)}
                style={{ border: 'none', background: 'transparent', fontSize: '24px', color: '#64748b', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '12px 18px', overflowY: 'auto', flex: 1 }}>
              {selectedEmployeeDocs.map((doc) => (
                <div key={doc._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 8px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 8, background: '#fbfbfd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                      {getCategoryIcon(doc.category)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{doc.title || 'Untitled document'}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{doc.category} • {formatDocumentDate(doc.createdAt)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleView(doc)}
                      style={{
                        border: '1px solid #dbeafe',
                        background: '#eff6ff',
                        color: '#3730a3',
                        borderRadius: '10px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDownload(doc._id, doc.filename)}
                      style={{
                        border: '1px solid #d1fae5',
                        background: '#fff',
                        color: '#374151',
                        borderRadius: '10px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Download
                    </button>
                    {canApprove && doc.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleUpdateStatus(doc._id, 'approved')}
                          style={{
                            border: '1px solid #a7f3d0',
                            background: '#d1fae5',
                            color: '#065f46',
                            borderRadius: '10px',
                            padding: '8px 12px',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(doc._id, 'rejected')}
                          style={{
                            border: '1px solid rgba(239,68,68,0.2)',
                            background: 'rgba(239,68,68,0.12)',
                            color: '#b91c1c',
                            borderRadius: '10px',
                            padding: '8px 12px',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(doc._id)}
                        style={{
                          border: '1px solid rgba(239,68,68,0.2)',
                          background: 'rgba(239,68,68,0.12)',
                          color: '#b91c1c',
                          borderRadius: '10px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ padding: '12px 18px', borderTop: '1px solid #eef2f7', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowEmployeeModal(false)}
                style={{
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  color: '#475569',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingDocument && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowEditModal(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '560px',
              borderRadius: '18px',
              background: '#fff',
              overflow: 'hidden',
              boxShadow: '0 24px 48px rgba(15,23,42,0.18)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '22px 24px', borderBottom: '1px solid #eef2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>Edit Document</h2>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#64748b' }}>Update title, category, or description for this document.</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                style={{ border: 'none', background: 'transparent', fontSize: '24px', color: '#64748b', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdate} style={{ padding: '20px 24px', display: 'grid', gap: '16px' }}>
              <div style={{ display: 'grid', gap: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Title</label>
                <input
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'grid', gap: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Category</label>
                <select
                  value={editFormData.category}
                  onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                >
                  <option value="Address Proof">Address Proof</option>
                  <option value="Educational Certificates">Educational Certificates</option>
                  <option value="PAN Card">PAN Card</option>
                  <option value="Bank Account">Bank Account</option>
                  <option value="Experience Letter">Experience Letter</option>
                  <option value="Offer Letter">Offer Letter</option>
                  <option value="Relieving Letter">Relieving Letter</option>
                  <option value="Screenshot of Resignation Email">Screenshot of Resignation Email</option>
                  <option value="Two Passport Size Photographs">Two Passport Size Photographs</option>
                  <option value="Last 3 Months' Salary Slips">Last 3 Months' Salary Slips</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div style={{ display: 'grid', gap: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Description</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={4}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{
                    border: '1px solid #e2e8f0',
                    background: '#fff',
                    color: '#475569',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    border: 'none',
                    background: 'linear-gradient(135deg, #3b775e 0%, #56a07a 100%)',
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowUploadModal(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: '650px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '15px',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                Upload Document for Employee
              </h2>
              <button
                onClick={() => setShowUploadModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUploadForEmployee}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Select Employee <span style={{ color: 'red' }}>*</span>
                </label>
                <div className="custom-upload-employee-dropdown" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setShowUploadEmployeeDropdown(!showUploadEmployeeDropdown)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: showUploadEmployeeDropdown ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      background: '#fff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      outline: 'none',
                      boxShadow: showUploadEmployeeDropdown ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                      transition: 'all 0.2s',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '14px',
                      color: uploadFormData.employeeId ? '#0f172a' : '#9ca3af'
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }}>
                      {uploadFormData.employeeId ? (() => {
                        const emp = employees.find(e => e._id === uploadFormData.employeeId);
                        return emp ? `${emp.firstName} ${emp.lastName}` : 'Choose an employee...';
                      })() : 'Choose an employee...'}
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: showUploadEmployeeDropdown ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {showUploadEmployeeDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      left: 0,
                      width: '100%',
                      background: '#ffffff',
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      zIndex: 300,
                      padding: '8px',
                      border: '1px solid #f1f5f9',
                      animation: 'pulse 0.2s ease-out'
                    }}>
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={uploadEmployeeSearchText}
                        onChange={(e) => setUploadEmployeeSearchText(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '13px',
                          background: '#f8fafc',
                          outline: 'none',
                          marginBottom: '8px',
                          fontFamily: "'Inter', sans-serif"
                        }}
                      />
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {employees
                          .filter(e => {
                            const search = uploadEmployeeSearchText.toLowerCase();
                            return e.firstName.toLowerCase().includes(search) ||
                              e.lastName.toLowerCase().includes(search) ||
                              (e.employeeId && e.employeeId.toLowerCase().includes(search));
                          })
                          .map(emp => (
                            <div
                              key={emp._id}
                              onClick={() => { setUploadFormData({ ...uploadFormData, employeeId: emp._id }); setShowUploadEmployeeDropdown(false); setUploadEmployeeSearchText(''); }}
                              style={{
                                padding: '10px 12px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: uploadFormData.employeeId === emp._id ? '600' : '400',
                                cursor: 'pointer',
                                color: uploadFormData.employeeId === emp._id ? '#166534' : '#334155',
                                background: uploadFormData.employeeId === emp._id ? '#dcfce7' : 'transparent',
                                marginBottom: '2px',
                                transition: 'background 0.1s'
                              }}
                              onMouseEnter={(e) => { if (uploadFormData.employeeId !== emp._id) e.target.style.background = '#f1f5f9'; }}
                              onMouseLeave={(e) => { if (uploadFormData.employeeId !== emp._id) e.target.style.background = 'transparent'; }}
                            >
                              {emp.firstName} {emp.lastName} <span style={{ color: '#94a3b8', fontSize: '12px' }}>({emp.employeeId})</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  {/* Hidden input to maintain native HTML required validation */}
                  <input type="hidden" value={uploadFormData.employeeId} required />
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Document Category <span style={{ color: 'red' }}>*</span>
                </label>
                <div className="custom-category-dropdown" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: showCategoryDropdown ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                      borderRadius: '8px',
                      background: '#fff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      outline: 'none',
                      boxShadow: showCategoryDropdown ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                      transition: 'all 0.2s',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '14px',
                      color: uploadFormData.category ? '#0f172a' : '#9ca3af'
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }}>
                      {uploadFormData.category || 'Select Category...'}
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: showCategoryDropdown ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {showCategoryDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      left: 0,
                      width: '100%',
                      background: '#ffffff',
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      zIndex: 300,
                      padding: '8px',
                      border: '1px solid #f1f5f9',
                      animation: 'pulse 0.2s ease-out'
                    }}>
                      <input
                        type="text"
                        placeholder="Search categories..."
                        value={categorySearchText}
                        onChange={(e) => setCategorySearchText(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '13px',
                          background: '#f8fafc',
                          outline: 'none',
                          marginBottom: '8px',
                          fontFamily: "'Inter', sans-serif"
                        }}
                      />
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {['Address Proof', 'Educational Certificates', 'PAN Card', 'Bank Account', 'Experience Letter', 'Offer Letter', 'Relieving Letter', 'Screenshot of Resignation Email', 'Two Passport Size Photographs', "Last 3 Months' Salary Slips", 'Other']
                          .filter(c => c.toLowerCase().includes(categorySearchText.toLowerCase()))
                          .map(cat => (
                            <div
                              key={cat}
                              onClick={() => { setUploadFormData({ ...uploadFormData, category: cat, subCategory: (cat === 'Address Proof' || cat === 'Educational Certificates') ? uploadFormData.subCategory : '' }); setShowCategoryDropdown(false); setCategorySearchText(''); }}
                              style={{
                                padding: '10px 12px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: uploadFormData.category === cat ? '600' : '400',
                                cursor: 'pointer',
                                color: uploadFormData.category === cat ? '#166534' : '#334155',
                                background: uploadFormData.category === cat ? '#dcfce7' : 'transparent',
                                marginBottom: '2px',
                                transition: 'background 0.1s'
                              }}
                              onMouseEnter={(e) => { if (uploadFormData.category !== cat) e.target.style.background = '#f1f5f9'; }}
                              onMouseLeave={(e) => { if (uploadFormData.category !== cat) e.target.style.background = 'transparent'; }}
                            >
                              {cat}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  {/* Hidden input to maintain native HTML required validation */}
                  <input type="hidden" value={uploadFormData.category} required />
                </div>
              </div>

              {(uploadFormData.category === 'Address Proof' || uploadFormData.category === 'Educational Certificates') && (
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Sub Category <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className="custom-subcategory-dropdown" style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setShowSubCategoryDropdown(!showSubCategoryDropdown)}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: showSubCategoryDropdown ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        background: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        outline: 'none',
                        boxShadow: showSubCategoryDropdown ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                        transition: 'all 0.2s',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: uploadFormData.subCategory ? '#0f172a' : '#9ca3af'
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }}>
                        {uploadFormData.subCategory || 'Select Sub Category...'}
                      </span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: showSubCategoryDropdown ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {showSubCategoryDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        left: 0,
                        width: '100%',
                        background: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        zIndex: 300,
                        padding: '8px',
                        border: '1px solid #f1f5f9',
                        animation: 'pulse 0.2s ease-out'
                      }}>
                        <input
                          type="text"
                          placeholder="Search sub categories..."
                          value={subCategorySearchText}
                          onChange={(e) => setSubCategorySearchText(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '13px',
                            background: '#f8fafc',
                            outline: 'none',
                            marginBottom: '8px',
                            fontFamily: "'Inter', sans-serif"
                          }}
                        />
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {(uploadFormData.category === 'Address Proof' 
                            ? ['Aadhar Card', 'Driving License', 'Voter id card', 'Passport', 'Electricity Bill', 'Wi-Fi Bill']
                            : ['10th Marksheet', '12th Marksheet', 'Graduation', 'Post Graduation'])
                            .filter(c => c.toLowerCase().includes(subCategorySearchText.toLowerCase()))
                            .map(subCat => (
                              <div
                                key={subCat}
                                onClick={() => { setUploadFormData({ ...uploadFormData, subCategory: subCat }); setShowSubCategoryDropdown(false); setSubCategorySearchText(''); }}
                                style={{
                                  padding: '10px 12px',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  fontWeight: uploadFormData.subCategory === subCat ? '600' : '400',
                                  cursor: 'pointer',
                                  color: uploadFormData.subCategory === subCat ? '#166534' : '#334155',
                                  background: uploadFormData.subCategory === subCat ? '#dcfce7' : 'transparent',
                                  marginBottom: '2px',
                                  transition: 'background 0.1s'
                                }}
                                onMouseEnter={(e) => { if (uploadFormData.subCategory !== subCat) e.target.style.background = '#f1f5f9'; }}
                                onMouseLeave={(e) => { if (uploadFormData.subCategory !== subCat) e.target.style.background = 'transparent'; }}
                              >
                                {subCat}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    <input type="hidden" value={uploadFormData.subCategory} required={uploadFormData.category === 'Address Proof' || uploadFormData.category === 'Educational Certificates'} />
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Title
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={uploadFormData.title}
                  onChange={(e) => setUploadFormData({ ...uploadFormData, title: e.target.value })}
                  placeholder="Enter document title (optional)"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Description
                </label>
                <textarea
                  className="form-control"
                  value={uploadFormData.description}
                  onChange={(e) => setUploadFormData({ ...uploadFormData, description: e.target.value })}
                  placeholder="Enter document description (optional)"
                  rows="3"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Choose File <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) => setUploadFormData({ ...uploadFormData, file: e.target.files[0] })}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  required
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Allowed formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max 10MB)
                </small>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documents;
